import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import LoadingLogo from "../components/LoadingLogo";
import {
  isSuperadminModeEnabled,
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
} from "../components/SuperadminHeader";

interface SuperadminRouteProps {
  children: React.ReactElement;
}

const SuperadminRoute: React.FC<SuperadminRouteProps> = ({ children }) => {
  const location = useLocation();
  const { lotId } = useParams<{ lotId: string }>();
  const { user, loading } = useUser();
  const [modeEnabled, setModeEnabled] = useState<boolean>(() => isSuperadminModeEnabled());

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setModeEnabled(detail.enabled);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setModeEnabled(event.newValue === "true");
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  if (loading || !user) {
    return <LoadingLogo text="Checking permissions..." />;
  }

  const isSuperadmin = (user.role || "").toLowerCase() === "superadmin";

  if (!isSuperadmin || !modeEnabled) {
    const fallback = lotId ? `/lot/${lotId}/dashboard` : "/dashboard";
    return <Navigate to={fallback} state={{ from: location }} replace />;
  }

  return children;
};

export default SuperadminRoute;
